import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button as AntButton,
  Descriptions,
  Modal,
  Table,
  TableColumnsType,
} from 'antd';
import useAxios from '../../../../hooks/useAxios';
import {
  IGetPaymentDetail,
  IPaymentDetails,
} from '../../../../models/payments.model';
import { getPaymentDetail } from '../../../../api/payments.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import moment from 'moment';
import { getCommonCodeLabel } from '../../../../lib/store';
import Button from '../../../Button';
import config from '../../../../lib/config';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  paymentRecordCode: number | undefined;
}

const PaymentDetailModal: FC<IProps> = ({
  visible,
  setVisible,
  paymentRecordCode,
}) => {
  const { t } = useTranslation();
  const { data: res, isLoading, error, execute } = useAxios<IGetPaymentDetail>();

  const [data, setData] = useState<any>();

  useEffect(() => {
    if (paymentRecordCode) {
      execute(getPaymentDetail(paymentRecordCode));
    }
  }, [paymentRecordCode]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data[0]);
    }
  }, [res, isLoading]);

  const onShowReceipt = () => {
    if (!data) return;

    let url = data.receiptUrl;
    if (!url.includes('http')) {
      url = config.posReceiptUrl + url;
    }

    window.open(url, '_blank');
  };

  const columns: TableColumnsType<IPaymentDetails> = [
    {
      title: t('common.column.semester'),
      dataIndex: 'semesterName',
      key: 'semesterName',
      align: 'center',
    },
    {
      title: t('payment.charge.column1'),
      dataIndex: 'monthNameCode',
      key: 'monthNameCode',
      align: 'center',
      render: (value: string) =>
        getCommonCodeLabel('SY0009', value?.trim() || '', 'ko'),
    },
    {
      title: t('common.menu.class'),
      dataIndex: '',
      key: '',
      align: 'center',
      render: () => data?.className,
    },
    {
      title: t('payment.charge.column3'),
      dataIndex: 'paymentTargetCode',
      key: 'paymentTargetCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('PA0001', value, 'ko'),
    },
    {
      title: t('payment.record.column1'),
      dataIndex: 'paymentMtdCode',
      key: 'paymentMtdCode',
      align: 'center',
      render: () =>
        getCommonCodeLabel('PA0002', data?.paymentMtdCode || '', 'ko'),
    },
    {
      title: t('payment.charge.column5'),
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: t('payment.record.column2'),
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      align: 'center',
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: t('payment.record.column3'),
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      align: 'center',
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: t('common.column.status'),
      dataIndex: 'paymentSttCode',
      key: 'paymentSttCode',
      align: 'center',
      render: (value: string) => getCommonCodeLabel('PA0003', value, 'ko'),
    },
    {
      title: t('payment.charge.column7'),
      dataIndex: 'paymentCloseData',
      key: 'paymentCloseData',
      align: 'center',
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <Modal
        title={t('payment.record.title2')}
        visible={visible}
        onCancel={() => setVisible(false)}
        width={750}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="primary" className='btn-basic pink' onClick={() => setVisible(false)}>
              {t('common.button.confirm')}
            </AntButton>
          </div>
        ]}
      >
        <div className='comm-table__area rowType mt-0'>
          <Descriptions
            bordered
            size="small"
            column={2}
            labelStyle={{ width: '25%' }}
            contentStyle={{ width: '25%' }}
          >
            <Descriptions.Item label="#" span={23}>
              {paymentRecordCode || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.retire.column1')}>
              {data?.memberName}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.charge.placeholder1')}>
              {data?.className || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.record.column3')}>
              {data?.totalPaymentAmount?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.record.column4')}>
              {data?.paymentDttm
                ? moment(data.paymentDttm).format('YYYY.MM.DD HH:mm')
                : ''}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.record.column1')}>
              {getCommonCodeLabel('PA0002', data?.paymentMtdCode || '', 'ko')}
            </Descriptions.Item>
            <Descriptions.Item label={t('payment.charge.placeholder2')}>
              {getCommonCodeLabel('PA0004', data?.paymentChannelCode || '', 'ko')}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Table
          className='comm-table__area'
          columns={columns}
          dataSource={data?.details}
          pagination={{
            pageSize: 999999999,
          }}
        />

        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink-line"
              onClick={onShowReceipt}
              text={t('payment.record.button1')}
              disabled={!data?.receiptUrl}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PaymentDetailModal;
